import type {
	DashboardtypesQueryDTO,
	TelemetrytypesSignalDTO,
} from 'api/generated/services/sigNoz.schemas';
import type { BuilderQuery } from 'types/api/v5/queryRange';

import {
	isBuilderEnvelope,
	isBuilderPluginKind,
} from 'lib/query/builderEnvelope';

/**
 * Flattens a panel's queries into its builder queries, unwrapping
 * `CompositeQuery` envelopes. Non-builder kinds (PromQL, ClickHouseSQL, Formula,
 * TraceOperator) are dropped — they lack the legend/groupBy/aggregation context
 * downstream code needs. Returns the generated v5 `BuilderQuery` shape directly.
 */
export function getBuilderQueries(
	queries: DashboardtypesQueryDTO[],
): BuilderQuery[] {
	const flattened: BuilderQuery[] = [];
	queries.forEach((envelope) => {
		const plugin = envelope.spec.plugin;
		if (isBuilderPluginKind(plugin.kind)) {
			flattened.push(plugin.spec as BuilderQuery);
			return;
		}
		if (plugin.kind === 'signoz/CompositeQuery') {
			(plugin.spec.queries || []).forEach((sub) => {
				if (isBuilderEnvelope(sub)) {
					flattened.push(sub.spec as BuilderQuery);
				}
			});
		}
	});
	return flattened;
}

/**
 * Datasource signal scoping panel-type compatibility (List needs logs/traces, not
 * metrics): the builder query's signal if present; else `defaultSignal` for a new
 * panel (queries empty until edited); else undefined for PromQL/ClickHouse.
 */
export function resolveSignal(
	queries: DashboardtypesQueryDTO[],
	defaultSignal: TelemetrytypesSignalDTO,
): TelemetrytypesSignalDTO | undefined {
	const builderSignal = getBuilderQueries(queries)[0]?.signal as
		| TelemetrytypesSignalDTO
		| undefined;
	if (builderSignal) {
		return builderSignal;
	}
	return queries.length ? undefined : defaultSignal;
}
